/* eslint-disable */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  VStack,
  Heading,
  Text,
  Radio,
  RadioGroup,
  Stack,
  useToast,
  useColorModeValue} from '@chakra-ui/react';
import axios from 'axios';
import { useModalToast } from "../../context/GlobalModalToast";

const Quiz = ({ courseId, quizContent, onComplete, onClose }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const { toast } = useModalToast();

  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  const [randomizedQuestions, setRandomizedQuestions] = useState([]);

  // Normalize quizContent to always be an array of questions
  useEffect(() => {
    let rawQuestions = [];
    if (Array.isArray(quizContent)) {
      rawQuestions = [...quizContent];
    } else if (quizContent && typeof quizContent === 'object') {
      rawQuestions = [...(quizContent.questions || quizContent.quiz || Object.values(quizContent)[0] || [])];
    }

    if (rawQuestions.length > 0) {
      // Shuffle the questions (Fisher-Yates)
      for (let i = rawQuestions.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [rawQuestions[i], rawQuestions[j]] = [rawQuestions[j], rawQuestions[i]];
      }
      // Pick 10 max
      setRandomizedQuestions(rawQuestions.slice(0, 10));
    }
  }, [quizContent]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [quizResult, setQuizResult] = useState(null);

  const handleNext = () => {
    const newAnswers = [...answers];
    newAnswers[currentQuestion] = selectedOption;
    setAnswers(newAnswers);

    if (currentQuestion < randomizedQuestions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setSelectedOption(null);
    } else {
      submitQuiz(newAnswers);
    }
  };

  const submitQuiz = async (finalAnswers) => {
    setIsSubmitting(true);
    try {
      const token = JSON.parse(localStorage.getItem('user'))?.token || "";
      
      const structuredAnswers = randomizedQuestions.map((q, idx) => ({
        questionText: q.question,
        answerIndex: finalAnswers[idx]
      }));

      const res = await axios.post(`${process.env.REACT_APP_API_URL}/api/enrollments/submit-quiz`, {
        courseId,
        answers: structuredAnswers
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.data.passed) {
        toast({
          title: 'Quiz Passed!',
          description: `Score: ${res.data.score}%`,
          status: 'success',
          duration: 5000
        });
        onComplete();
      } else {
        setQuizResult({ passed: false, score: res.data.score });
      }
    } catch (err) {
      console.error(err);
      toast({
        title: 'Submission Failed',
        description: 'Failed to submit quiz. Please try again.',
        status: 'error',
        duration: 3000
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRetry = () => {
    setCurrentQuestion(0);
    setAnswers([]);
    setSelectedOption(null);
    setQuizResult(null);
  };

  if (!randomizedQuestions || randomizedQuestions.length === 0) {
    return (
      <Box p={8} textAlign="center">
        <Text fontSize="lg" mb={4}>Preparing your corporate certification quiz...</Text>
        <Text color="gray.500">Generating 10 MCQs based on course content.</Text>
      </Box>
    );
  }

  if (quizResult && !quizResult.passed) {
    return (
      <Box p={8} borderWidth={1} borderColor={borderColor} borderRadius="lg" bg={bgColor} shadow="md" textAlign="center">
        <VStack spacing={6}>
          <Heading size="lg" color="red.500">Quiz Failed</Heading>
          <Text fontSize="md">You scored <strong>{quizResult.score}%</strong>.</Text>
          <Text fontSize="sm" color="gray.500">A passing score is required to earn your certificate.</Text>
          <Stack direction="row" spacing={4} justify="center">
            <Button colorScheme="purple" onClick={handleRetry}>Retry Quiz</Button>
            <Button variant="outline" onClick={() => { if(onClose) onClose(); else window.location.reload(); }}>Close</Button>
          </Stack>
        </VStack>
      </Box>
    );
  }

  const currentQuizData = randomizedQuestions[currentQuestion];

  if (!currentQuizData) {
    return <Box p={8} textAlign="center">Error loading question data. Please refresh.</Box>;
  }

  return (
    <Box p={8} borderWidth={1} borderColor={borderColor} borderRadius="lg" bg={bgColor} shadow="md">
      <VStack spacing={6} align="stretch">
        <Heading size="md">Question {currentQuestion + 1} of {randomizedQuestions.length}</Heading>
        <Text fontSize="lg" fontWeight="bold">{currentQuizData.question}</Text>
        
        <RadioGroup onChange={(val) => setSelectedOption(parseInt(val))} value={selectedOption?.toString()}>
          <Stack spacing={4}>
            {currentQuizData.options.map((opt, idx) => (
              <Radio key={idx} value={idx.toString()}>{opt}</Radio>
            ))}
          </Stack>
        </RadioGroup>

        <Button 
          colorScheme="blue" 
          onClick={handleNext} 
          isDisabled={selectedOption === null}
          isLoading={isSubmitting}
          loadingText="Submitting..."
        >
          {currentQuestion < randomizedQuestions.length - 1 ? 'Next Question' : 'Submit Quiz'}
        </Button>
      </VStack>
    </Box>
  );
};

export default Quiz;
